import { FC, useEffect, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'Components/UI';
import ListContainer from 'Components/UI/Containers/ListContainer/ListContainer';
import Button from 'Components/UI/Button';
import Select from 'Components/UI/Select';
import Form from 'Components/UI/Form/Form';
import Field from 'Components/UI/Field';
import schema from './UsuariosConvidadosTab.schema';
import InfoTooltip from 'Components/UI/InfoTooltip';
import { ITenantRole } from 'Data/interfaces/Tenant/ITenantRole';
import { GetAvatar } from 'Utils/generateThumbnail';
import toastHandler from 'Utils/toastHandler';
import { REPEATED_EMAILS_ERROR } from 'Utils/Messages';
import { IUpdatedUsers } from '../../../Modals/CadastroObra';
import { IUserData } from 'Data/interfaces/User/IUserData';
import { ISharedUser } from 'Data/interfaces/User/ISharedUser';
import { IInvitedUser } from 'Data/interfaces/User/IInvitedUser';
import { useSelector } from 'react-redux';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import SearchInput from 'Components/UI/SearchInput';
import LoadingUsersPermissions from 'Components/UI/CustomLoading/LoadingUsersPermissions';
import { getTheme } from 'Store/MultiDomain/MultiDomain.selector';
import { Container } from './styles';
import './UsuariosConvidadosTab-override.css';

interface IUsuariosConvidadosTab {
  isSubmited?: boolean;
  tenantRoles: ITenantRole[];
  TenantSharedUsers: IUserData[];
  sharedUsers: ISharedUser[];
  invitedUsers: IInvitedUser[];
  registerCompleted?: boolean;
  currentRole?: number;
  numberInvitedAllUsers?: number;
  maxUsers?: number;
  csId?: number;
  numberInvitedUsers?: number;
  firstInvitedUsers?: IInvitedUser[];
  isLoading?: boolean;
  changeUsers: (changedUser: IUpdatedUsers) => void;
  onConfirm?: () => void;
  onSubmit?: (props: any) => void;
}

const UsuariosConvidadosTab: FC<IUsuariosConvidadosTab> = ({
  isSubmited,
  tenantRoles,
  TenantSharedUsers,
  sharedUsers,
  invitedUsers,
  currentRole,
  numberInvitedAllUsers,
  firstInvitedUsers,
  isLoading,
  changeUsers,
  onSubmit,
  onConfirm,
}) => {
  const { t } = useTranslation('obras');
  const theme = useSelector(getTheme);
  const planFree = useSelector((state: IGlobalReducerState) => state.auth.planFree);
  const maxUsers = useSelector((state: IGlobalReducerState) =>
    state.auth.userInfo?.UserStoreItem.MaxQtyUsers
    || state.auth.userInfo?.UserStoreItem.StorePlan?.MaxQtyUsers
  );

  const getPrivilegesByRole = (roleId: number) => {
    const roleName = tenantRoles.find(x => x.TenantRoleId === roleId)?.RoleName;

    switch (roleName) {
      case 'Coordenador':
        return [0, 1, 2, 3, 4];
      case 'Engenheiro':
        return [0, 1, 2, 4];
      case 'Projetista':
        return [0, 1, 2];
      case 'Consultor':
        return [0];
    }
  }

  const TenantSharedUsersToSharedUsers = useCallback(() => {
    return TenantSharedUsers.filter((user) => user.CurrentRoleFk !== 1).map((user) => {
      const sharedUserSelected = sharedUsers?.find(
        (sharedUser) => sharedUser.UserFk === user.Id
      );
      if (sharedUserSelected !== undefined) {
        sharedUserSelected.isShared = true;
        return sharedUserSelected;
      }

      const privileges = getPrivilegesByRole(user.CurrentRoleFk ?? 0) ?? [];
      const hasPlottingPermission = privileges.indexOf(4) > -1;

      if (hasPlottingPermission)
        privileges.splice(privileges.indexOf(4), 1);

      return {
        User: user,
        HasPlottingPermission: hasPlottingPermission,
        isShared: false,
        UserFk: user.Id,
        UserId: user.Id,
        Privileges: privileges
      };
    }) as ISharedUser[];
  }, [TenantSharedUsers, sharedUsers]);

  const [InvitedUsers, setInvitedUsers] = useState(invitedUsers || []);
  const [SharedUsers, setSharedUsers] = useState(TenantSharedUsersToSharedUsers());
  const [RoleNewUser, setRoleNewUser] = useState<any>(4);
  const [infoUsuarioUpgradeVisible, setInfoUsuarioUpgradeVisible] = useState(false);
  const [newInviteds, setNewInviteds] = useState(0);
  const [searchUsers, setSearchUsers] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  const filteredInvitedUsers = useMemo(() => InvitedUsers?.filter((users: any) => (
    users.Email?.toLowerCase().includes(searchUsers?.toLowerCase())
  )), [InvitedUsers, searchUsers]);

  const filteredSharedUsers = useMemo(() => SharedUsers?.filter((users: any) => (
    users.User?.Nome?.toLowerCase().includes(searchUsers?.toLowerCase()) ||
    users.User?.Email?.toLowerCase().includes(searchUsers?.toLowerCase())
  )), [SharedUsers, searchUsers]);

  const getSelectedSharedUsers = () => {
    return SharedUsers.filter((sharedUser) => sharedUser.isShared);
  };

  const updateUsers = () => {
    changeUsers({ InvitedUsers, SharedUsers: getSelectedSharedUsers() });
  };

  const hasRepeatedEmail = (email: string) => {
    return (
      InvitedUsers.some((user) => user.Email.toLowerCase() === email.toLowerCase()) ||
      SharedUsers.some(
        (sharedUser) => sharedUser?.User?.Email?.toLowerCase() === email.toLowerCase()
      )
    );
  };

  const changeSharedUsers = (shared: ISharedUser) => {
    const index = SharedUsers.findIndex(user => user.UserFk === shared.UserFk);
    SharedUsers[index].isShared = !SharedUsers[index].isShared;
    setSharedUsers(JSON.parse(JSON.stringify(SharedUsers)));
    updateUsers();
  };

  const addInvitedUser = (data: any) => {
    if (hasRepeatedEmail(data.Email)) {
      toastHandler.showError(REPEATED_EMAILS_ERROR());
    } else {

      const privileges = getPrivilegesByRole(RoleNewUser) ?? [];
      const hasPlottingPermission = privileges.indexOf(4) > -1;

      if (hasPlottingPermission)
        privileges.splice(privileges.indexOf(4), 1);

      const newUser: IInvitedUser = {
        Email: data.Email.toLowerCase(),
        RoleFk: RoleNewUser,
        Privileges: privileges,
        HasPlottingPermission: hasPlottingPermission
      };
      InvitedUsers.push(newUser);
      setInvitedUsers(InvitedUsers);
      updateUsers();
    }
  };

  const removeInvitedUser = (invited: IInvitedUser) => {
    const index = InvitedUsers.findIndex(user => user.Email === invited.Email);
    InvitedUsers.splice(index, 1);
    setInvitedUsers(InvitedUsers);
    updateUsers();
  };

  const changeInvitedUserRole = (invited: IInvitedUser, role: any) => {
    const privilagesChange = getPrivilegesByRole(role) || [0, 1, 2]
    const index = InvitedUsers.findIndex(user => user.Email === invited.Email);
    InvitedUsers[index].Privileges = privilagesChange
    InvitedUsers[index].RoleFk = role;
    InvitedUsers[index].HasPlottingPermission = role === 7 ? true : false
    setInvitedUsers(JSON.parse(JSON.stringify(InvitedUsers)));
    updateUsers();
  };

  const getSharedUserRoleName = (roleFk?: number) => {
    const roleName = tenantRoles.find((role) => role.TenantRoleId === roleFk)?.RoleName;
    return roleName;
  };

  const roles = tenantRoles.map((role) => ({
    value: role.TenantRoleId,
    description: role.RoleName,
    label: role.RoleName
  }));

  const handleChangeSearchUsers = (value: string) => {
    setSearchUsers(value);
  }

  useEffect(() => {
    if (invitedUsers) {
      setInvitedUsers([...invitedUsers]);
    }
    setSharedUsers(TenantSharedUsersToSharedUsers());
  }, [TenantSharedUsersToSharedUsers, invitedUsers, sharedUsers]);

  useEffect(() => {
    if (InvitedUsers && firstInvitedUsers) {
      setNewInviteds(InvitedUsers.filter((user) => {
        return !firstInvitedUsers.map(userAll => userAll.Email).includes(user.Email);
      })?.length);
    }
    if (InvitedUsers && !firstInvitedUsers) {
      setNewInviteds(InvitedUsers.length);
    }
  }, [InvitedUsers, firstInvitedUsers]);

  useEffect(() => {
    if (maxUsers) {
      if (numberInvitedAllUsers && ((numberInvitedAllUsers + newInviteds) >= maxUsers)) {
        setInfoUsuarioUpgradeVisible(true);
      } else {
        setInfoUsuarioUpgradeVisible(false)
      }
    }
  }, [SharedUsers, InvitedUsers, newInviteds, numberInvitedAllUsers]);

  if (isSubmited && onSubmit) {
    onSubmit({ InvitedUsers, SharedUsers: getSelectedSharedUsers() });
  };

  return (
    <Container loading={isLoading}>
      <Form schema={schema} onSubmit={addInvitedUser} resetForm={true}>
        <div className="newUsersWrapper">
          {infoUsuarioUpgradeVisible && (
            <div className="convertionToPremiumModal">
              <p
                className="primaryTextConvertion"
                style={{ color: theme.colors.primary.primary }}
              >
                {currentRole === 1
                  ? t('cadastroObra.usuariosTab.upgradeWarningAdmin')
                  : t('cadastroObra.usuariosTab.upgradeWarningCompany')
                }
              </p>
              <p className="secondaryTextConvertion">
                {currentRole === 1
                  ? t('cadastroObra.usuariosTab.upgradeMsgAdmin')
                  : t('cadastroObra.usuariosTab.upgradeMsgCompany')
                }
              </p>
              <Button
                className="button"
                type="primary"
                height={37}
                onClick={onConfirm}
              >
                {currentRole === 1
                  ? t('cadastroObra.usuariosTab.acquirePremium')
                  : t('cadastroObra.usuariosTab.requestPremium')
                }
              </Button>
            </div>
          )}
          <Field
            name="Email"
            label={t('cadastroObra.usuariosTab.emailLabel')}
            placeholder={t('cadastroObra.usuariosTab.emailPlaceholder')}
            className="inputEmail"
            disabled={infoUsuarioUpgradeVisible ? true : false}
          />
          <Select
            onSelect={setRoleNewUser}
            className="selectRole"
            placeholder={t('cadastroObra.usuariosTab.selectRole')}
            defaultValue={RoleNewUser}
            options={roles}
            disabled={infoUsuarioUpgradeVisible ? true : false}
          />
          <Button
            htmlType="submit"
            className="buttonNewUser"
            type="primary"
            height={34}
            disabled={(infoUsuarioUpgradeVisible || searchLoading) ? true : false}
          >
            {t('cadastroObra.usuariosTab.invite')}
          </Button>
        </div>
      </Form>
      <div className="permissionsHintWrapper">
        <span>{t('cadastroObra.usuariosTab.needHelpRoles')}</span>
        <InfoTooltip
          placement="bottom"
          overlay={
            <div className="hintText">
              {t('cadastroObra.usuariosTab.rolesHelpTooltip')}
            </div>
          }
        >
          <div>
            <Icon
              icon="informacao"
              color="cinzaPadrao"
              customSize={12}
              className="iconInfo"
            />
          </div>
        </InfoTooltip>
      </div>
      <div>
        <h3 className="titleSharedUserList">
          {t('cadastroObra.usuariosTab.selectUsersToShare')}
        </h3>
        {planFree && (
          <p className="titleSharedUserListFreemium">
            {t('cadastroObra.usuariosTab.freeUserLimitInfo', { count: (maxUsers ?? 5) - 1 })}
          </p>
        )}
        <div>
          <div className="TopbarInput">
            <SearchInput
              setSearch={handleChangeSearchUsers}
              setSearchLoading={setSearchLoading}
              inputPlaceholder={t('cadastroObra.usuariosTab.searchUserPlaceholder')}
            />
          </div>
        </div>
        {(isLoading || searchLoading) && (
          <LoadingUsersPermissions multiple={2} />
        )}
        {!isLoading && !searchLoading && (
          <>
            {((filteredInvitedUsers.length === 0) &&
              (filteredSharedUsers.length === 0) &&
              searchUsers) ? (
              <div className="mainWrapper">
                <span className="textBottomHead">
                  {t('cadastroObra.usuariosTab.noUserFound')}
                </span>
                <span className="textBottomFooter">
                  {t('cadastroObra.usuariosTab.tryAnotherSearch')}
                </span>
              </div>
            ) : (
              <div>
                {filteredInvitedUsers.map((user, index) => (
                  <ListContainer
                    height={51}
                    key={`invitedUser-${index}`}
                    className="sharedUserItem"
                  >
                    <div className="invitedUserWrapper">
                      <div>
                        <GetAvatar name={user.Email} />
                        <span className="invitedUserName">{user.Email}</span>
                      </div>

                      <div className="selectionsWrapper">
                        <div>
                          <Select
                            onSelect={(value) => changeInvitedUserRole(user, value)}
                            className="selectRole"
                            placeholder={t('cadastroObra.usuariosTab.selectRolePlaceholder')}
                            defaultValue={user.RoleFk}
                            options={roles}
                          />
                        </div>

                        <div>
                          <div className="waitingButton">
                            <span className="waitingTitle">{t('cadastroObra.usuariosTab.waiting')}</span>
                            <Button
                              className="buttonRemoveUser"
                              height={32}
                              onClick={() => removeInvitedUser(user)}
                            >
                              <Icon
                                icon="cancelar"
                                customSize={8}
                                color="primary"
                              />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </ListContainer>
                ))}
                {filteredSharedUsers.map((shared, index) => (
                  <ListContainer
                    height={51}
                    key={`sharedUser-${index}`}
                    className="sharedUserItem"
                  >
                    <div className="invitedUserWrapper">
                      <GetAvatar
                        name={shared.User?.Nome || shared.User?.Email || ''}
                        shape='circle'
                        src={shared.User?.OriginalImageUrl}
                        thumbSrc={shared.User?.ThumbnailImageUrl}
                        thumbType="small"
                      />
                      <span className="sharedUserName">
                        {shared.User?.Nome || shared.User?.Email}
                      </span>
                      <div className="sharedRoleName">
                        {getSharedUserRoleName(shared.User?.CurrentRoleFk)}
                      </div>
                      <Button
                        height={30}
                        customText
                        isShared={shared.isShared}
                        className={`shareButton ${shared.isShared ? 'shared' : ''}`}
                        onClick={() => changeSharedUsers(shared)}
                      >
                        {shared.isShared ? (
                          <>
                            <span>{t('cadastroObra.usuariosTab.shared')}</span>
                            <Icon
                              icon="confirmar"
                              color="verde"
                              customSize={9}
                              style={{ height: 9 }}
                            />
                          </>
                        ) : (
                          t('cadastroObra.usuariosTab.share')
                        )}
                      </Button>
                    </div>
                  </ListContainer>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default UsuariosConvidadosTab;
